import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import Header from "../../components/header/Header";
import Footer from "../../components/footer/Footer";
import { MenuBar } from "../../components/Menu/Menu";
import Meeting from "../../components/Table/Meeting";
import {
  getCompletedMeetingAsStudent,
  getCompletedMeetingAsTeacher,
  getPendingMeetingAsStudent,
  getPendingMeetingAsTeacher,
} from "../../store/actions/meeting.action";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

function MonthFilter({ month, year, lastYear, onMonthChange, onLastYear }) {
  const { t } = useTranslation();
  const now = new Date();
  const currentMonth = now.getMonth() + 1;
  const currentYear = now.getFullYear();

  let startMonth = currentMonth + 1;
  let startYear = currentYear;
  if (startMonth > 12) {
    startMonth = 1;
    startYear += 1;
  }

  const months = [];
  for (let i = 0; i < 12; i++) {
    let m = startMonth - i;
    let y = startYear;
    if (m <= 0) {
      m += 12;
      y -= 1;
    }
    months.push({ month: m, year: y });
  }

  const monthNames = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
  ];

  const selectedValue = lastYear ? "last_year" : (month && year ? `${year}-${month}` : "all");

  return (
    <div className="flex flex-wrap items-center gap-2 mb-4">
      <select
        value={selectedValue}
        onChange={(e) => {
          const val = e.target.value;
          if (val === "last_year") {
            onLastYear();
          } else if (val === "all") {
            onMonthChange(null, null);
          } else {
            const [y, m] = val.split("-").map(Number);
            onMonthChange(m, y);
          }
        }}
        className="border border-gray-300 rounded-lg px-3 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#6C63FF]"
      >
        <option value="all">
          Filter by month
        </option>
        {months.map(({ month: m, year: y }) => (
          <option key={`${y}-${m}`} value={`${y}-${m}`}>
            {monthNames[m - 1]} {y}
          </option>
        ))}
        <option value="last_year">
          Last 12 Months
        </option>
      </select>
      {selectedValue !== "all" && (
        <button
          onClick={() => onMonthChange(null, null)}
          className="px-3 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
        >
          Clear Filter
        </button>
      )}
      {!lastYear && (
        <button
          onClick={onLastYear}
          className="px-3 py-2 text-sm font-medium text-[#6C63FF] border border-[#6C63FF] rounded-lg hover:bg-[#6C63FF] hover:text-white transition-colors"
        >
          Last 12 Months
        </button>
      )}
    </div>
  );
}

export default function Meetings() {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const loginUrl =
    typeof window !== "undefined" && window.location
      ? `${window.location.origin}/login`
      : "/login";

  const dispatch = useDispatch();
  const token = localStorage.getItem("token");
  const navigation = useNavigate();

  useEffect(() => {
    if (!token) {
      navigation(`/login`);
    }
  }, [token, navigation]);

  // Page state for each table
  const [completedPageTeacher, setCompletedPageTeacher] = useState(1);
  const [pendingPageTeacher, setPendingPageTeacher] = useState(1);
  const [completedPageStudent, setCompletedPageStudent] = useState(1);
  const [pendingPageStudent, setPendingPageStudent] = useState(1);

  // Month/year filter state for each table (default to inactive / all months)
  const [completedTeacherFilter, setCompletedTeacherFilter] = useState({ month: null, year: null, lastYear: false });
  const [pendingTeacherFilter, setPendingTeacherFilter] = useState({ month: null, year: null, lastYear: false });
  const [completedStudentFilter, setCompletedStudentFilter] = useState({ month: null, year: null, lastYear: false });
  const [pendingStudentFilter, setPendingStudentFilter] = useState({ month: null, year: null, lastYear: false });

  const [loading, setLoading] = useState(false);

  const getPendingMeetingOfTeacher = useSelector(
    (state) => state.meeting.getPendingMeetingTeacher
  );
  const sortedPendingMeetingsOfTeacher = useMemo(() => {
    if (!getPendingMeetingOfTeacher?.results) return [];
    const sorted = [...getPendingMeetingOfTeacher.results].sort((a, b) =>
      new Date(b.meeting_happen_at) - new Date(a.meeting_happen_at)
    );
    if (!pendingTeacherFilter.month && !pendingTeacherFilter.year && !pendingTeacherFilter.lastYear) {
      return sorted.slice(0, 5);
    }
    return sorted;
  }, [getPendingMeetingOfTeacher, pendingTeacherFilter]);

  const getCompletedMeetingOfTeacher = useSelector(
    (state) => state.meeting.getCompletedMeetingTeacher
  );
  const sortedCompletedMeetingsOfTeacher = useMemo(() => {
    if (!getCompletedMeetingOfTeacher?.results) return [];
    const sorted = [...getCompletedMeetingOfTeacher.results].sort((a, b) =>
      new Date(b.meeting_happen_at) - new Date(a.meeting_happen_at)
    );
    if (!completedTeacherFilter.month && !completedTeacherFilter.year && !completedTeacherFilter.lastYear) {
      return sorted.slice(0, 5);
    }
    return sorted;
  }, [getCompletedMeetingOfTeacher, completedTeacherFilter]);

  const getPendingMeetingOfStudent = useSelector(
    (state) => state.meeting.getPendingMeetingStudent
  );
  const sortedPendingMeetingsOfStudent = useMemo(() => {
    if (!getPendingMeetingOfStudent?.results) return [];
    const sorted = [...getPendingMeetingOfStudent.results].sort((a, b) =>
      new Date(b.meeting_happen_at) - new Date(a.meeting_happen_at)
    );
    if (!pendingStudentFilter.month && !pendingStudentFilter.year && !pendingStudentFilter.lastYear) {
      return sorted.slice(0, 5);
    }
    return sorted;
  }, [getPendingMeetingOfStudent, pendingStudentFilter]);

  const getCompletedMeetingOfStudent = useSelector(
    (state) => state.meeting.getCompletedMeetingStudent
  );
  const sortedCompletedMeetingsOfStudent = useMemo(() => {
    if (!getCompletedMeetingOfStudent?.results) return [];
    const sorted = [...getCompletedMeetingOfStudent.results].sort((a, b) =>
      new Date(b.meeting_happen_at) - new Date(a.meeting_happen_at)
    );
    if (!completedStudentFilter.month && !completedStudentFilter.year && !completedStudentFilter.lastYear) {
      return sorted.slice(0, 5);
    }
    return sorted;
  }, [getCompletedMeetingOfStudent, completedStudentFilter]);

  // Fetch completed teacher
  useEffect(() => {
    if (token) {
      setLoading(true);
      dispatch(getCompletedMeetingAsTeacher({
        token,
        page: completedPageTeacher,
        month: completedTeacherFilter.lastYear ? undefined : completedTeacherFilter.month,
        year: completedTeacherFilter.lastYear ? undefined : completedTeacherFilter.year,
        lastYear: completedTeacherFilter.lastYear,
      })).finally(() => setLoading(false));
    }
  }, [completedPageTeacher, completedTeacherFilter, dispatch, token]);

  // Fetch pending teacher
  useEffect(() => {
    if (token) {
      setLoading(true);
      dispatch(getPendingMeetingAsTeacher({
        token,
        page: pendingPageTeacher,
        month: pendingTeacherFilter.lastYear ? undefined : pendingTeacherFilter.month,
        year: pendingTeacherFilter.lastYear ? undefined : pendingTeacherFilter.year,
        lastYear: pendingTeacherFilter.lastYear,
      })).finally(() => setLoading(false));
    }
  }, [pendingPageTeacher, pendingTeacherFilter, dispatch, token]);

  // Fetch completed student
  useEffect(() => {
    if (token) {
      setLoading(true);
      dispatch(getCompletedMeetingAsStudent({
        token,
        page: completedPageStudent,
        month: completedStudentFilter.lastYear ? undefined : completedStudentFilter.month,
        year: completedStudentFilter.lastYear ? undefined : completedStudentFilter.year,
        lastYear: completedStudentFilter.lastYear,
      })).finally(() => setLoading(false));
    }
  }, [completedPageStudent, completedStudentFilter, dispatch, token]);

  // Fetch pending student
  useEffect(() => {
    if (token) {
      setLoading(true);
      dispatch(getPendingMeetingAsStudent({
        token,
        page: pendingPageStudent,
        month: pendingStudentFilter.lastYear ? undefined : pendingStudentFilter.month,
        year: pendingStudentFilter.lastYear ? undefined : pendingStudentFilter.year,
        lastYear: pendingStudentFilter.lastYear,
      })).finally(() => setLoading(false));
    }
  }, [pendingPageStudent, pendingStudentFilter, dispatch, token]);

  const closeMenuBar = () => {
    setMenuOpen(false);
  };

  const openMenuBar = () => {
    setMenuOpen(true);
  };

  const PENDING_TABLE_HEAD = [
    "No",
    t("meeting.time"),
    t("meeting.length"),
    t("meeting.leader"),
    t("meeting.follower"),
    t("meeting.lang"),
    t("meeting.status"),
    t("meeting.zoom"),
    t("meeting.action"),
  ];

  const COMPLETED_TABLE_HEAD = [
    "No",
    t("meeting.time"),
    t("meeting.length"),
    t("meeting.leader"),
    t("meeting.follower"),
    t("meeting.lang"),
    t("meeting.status"),
    t("meeting.action"),
    "",
  ];

  const completedNumberOfPagesTeacher = Math.ceil(
    getCompletedMeetingOfTeacher?.count / 10
  );
  const pendingNumberOfPagesTeacher = Math.ceil(
    getPendingMeetingOfTeacher?.count / 10
  );
  const completedNumberOfPagesStudent = Math.ceil(
    getCompletedMeetingOfStudent?.count / 10
  );
  const pendingNumberOfPagesStudent = Math.ceil(
    getPendingMeetingOfStudent?.count / 10
  );

  const handleFilterChange = (setFilter, setPage) => ({
    onMonthChange: (m, y) => {
      setFilter({ month: m, year: y, lastYear: false });
      setPage(1);
    },
    onLastYear: () => {
      setFilter(prev => ({ ...prev, lastYear: true }));
      setPage(1);
    },
  });

  return (
    <div className="max-w-screen-2xl mx-auto p-4 h-full">
      <Header openMenu={openMenuBar} />
      {!token ? (
        <div className="text-xl lg:text-2xl mt-4 font-normal text-center py-2">
          <p>
            Please login ... at{" "}
            <a href={loginUrl} className="text-blue-500 hover:underline">
              {loginUrl}
            </a>
          </p>
        </div>
      ) : (
        <div className="w-full flex flex-col gap-12 p-4 md:p-12">
          <div className="flex flex-col gap-1">
            <p className="flex justify-center text-1xl md:text-2xl lg:text-2xl 1xl:text-2xl font-bold">
              {t("meeting.completed_meeting_teacher")}
            </p>
            <MonthFilter
              month={completedTeacherFilter.month}
              year={completedTeacherFilter.year}
              lastYear={completedTeacherFilter.lastYear}
              {...handleFilterChange(setCompletedTeacherFilter, setCompletedPageTeacher)}
            />
            <Meeting
              TABLE_HEAD={COMPLETED_TABLE_HEAD}
              setPage={setCompletedPageTeacher}
              page={completedPageTeacher}
              numberOfPages={completedNumberOfPagesTeacher}
              TABLE_ROWS={sortedCompletedMeetingsOfTeacher}
              isLoading={loading}
            />
          </div>
          <div className="flex flex-col gap-1">
            <p className="flex justify-center text-1xl md:text-2xl lg:text-2xl 1xl:text-2xl font-bold">
              {t("meeting.pending_meeting_teacher")}
            </p>
            <MonthFilter
              month={pendingTeacherFilter.month}
              year={pendingTeacherFilter.year}
              lastYear={pendingTeacherFilter.lastYear}
              {...handleFilterChange(setPendingTeacherFilter, setPendingPageTeacher)}
            />
            <Meeting
              TABLE_HEAD={PENDING_TABLE_HEAD}
              setPage={setPendingPageTeacher}
              page={pendingPageTeacher}
              numberOfPages={pendingNumberOfPagesTeacher}
              TABLE_ROWS={sortedPendingMeetingsOfTeacher}
              isLoading={loading}
            />
          </div>
          <div className="flex flex-col gap-1">
            <p className="flex justify-center text-1xl md:text-2xl lg:text-2xl 1xl:text-2xl font-bold">
              {t("meeting.completed_meeting_student")}
            </p>
            <MonthFilter
              month={completedStudentFilter.month}
              year={completedStudentFilter.year}
              lastYear={completedStudentFilter.lastYear}
              {...handleFilterChange(setCompletedStudentFilter, setCompletedPageStudent)}
            />
            <Meeting
              TABLE_HEAD={COMPLETED_TABLE_HEAD}
              setPage={setCompletedPageStudent}
              page={completedPageStudent}
              numberOfPages={completedNumberOfPagesStudent}
              TABLE_ROWS={sortedCompletedMeetingsOfStudent}
              isLoading={loading}
            />
          </div>
          <div className="flex flex-col gap-1">
            <p className="flex justify-center text-1xl md:text-2xl lg:text-2xl 1xl:text-2xl font-bold">
              {t("meeting.pending_meeting_student")}
            </p>
            <MonthFilter
              month={pendingStudentFilter.month}
              year={pendingStudentFilter.year}
              lastYear={pendingStudentFilter.lastYear}
              {...handleFilterChange(setPendingStudentFilter, setPendingPageStudent)}
            />
            <Meeting
              TABLE_HEAD={PENDING_TABLE_HEAD}
              setPage={setPendingPageStudent}
              page={pendingPageStudent}
              numberOfPages={pendingNumberOfPagesStudent}
              TABLE_ROWS={sortedPendingMeetingsOfStudent}
              isLoading={loading}
            />
          </div>
        </div>
      )}
      <Footer />
      <MenuBar open={menuOpen} close={closeMenuBar} />
    </div>
  );
}
